<template>
  <div>
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center py-2">
        <span class="text-md font-bold">Digital Profile</span>
        <v-btn icon @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>

      <v-card-text>
        <div>
          <!-- Profile Header -->
          <v-row class="p-4">
            <v-col cols="6">
              <div class="d-flex align-center">
                <!-- Left side: Avatar -->
                <UserAvatar
                  :user="employee"
                  :size="100"
                  :show-name="false"
                  :avatar-style="{ width: '100px', height: '100px' }"
                  class="w-1"
                />

                <!-- Middle: Name, EmpId, Designation, Department -->
                <div class="ml-4">
                  <div class="text-h6 font-weight-bold">
                    {{ employee.firstName }} {{ employee.lastName }} / {{ employee.employeeId }}
                  </div>
                  <div class="text-body-2">
                    {{ employee.department?.departmentName || 'N/A' }} ,
                    {{ employee.designation?.designationName || 'N/A' }}
                  </div>
                </div>

                <v-spacer></v-spacer>

                <!-- Right side: Status -->
                <div class="text-body-2 text-gray-600 dark-text-color">
                  <strong>Status</strong><br />
                  <v-chip small :color="getStatusColor(employee.status)" outlined>
                    {{ employee.status }}
                  </v-chip>
                </div>
              </div>
            </v-col>
          </v-row>

          <v-divider></v-divider>

          <!-- Personal Information -->
          <v-row class="mt-6">
            <v-col cols="12" md="4" class="p-0">
              <v-list dense>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">Email</v-list-item-title>
                    <v-list-item-subtitle>{{
                      employee.email || employee.maskedEmail || 'N/A'
                    }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">Mobile</v-list-item-title>
                    <v-list-item-subtitle>{{
                      employee.mobile || employee.maskedMobileNumber || 'N/A'
                    }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>
            <v-col cols="12" md="4" class="p-0">
              <v-list dense>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">Date of Birth</v-list-item-title>
                    <v-list-item-subtitle>{{
                      employee.dob ? formatDate(employee.dob) : 'N/A'
                    }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">Gender</v-list-item-title>
                    <v-list-item-subtitle>{{ employee.gender || 'N/A' }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>
            <v-col cols="12" md="4" class="p-0">
              <v-list dense>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">Role</v-list-item-title>
                    <v-list-item-subtitle>{{
                      employee.wfmRole?.role || 'N/A'
                    }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">Employee Type</v-list-item-title>
                    <v-list-item-subtitle>{{
                      employee.employeeType || 'N/A'
                    }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>

          <!-- Address Information -->
          <v-row class="mt-4">
            <v-col cols="12" class="p-0">
              <h3 class="text-h6 font-weight-bold px-4 underline">Address Information</h3>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="4" class="p-0">
              <v-list dense>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">Address</v-list-item-title>
                    <v-list-item-subtitle>
                      {{ employee.addressLine1 || 'N/A'
                      }}<span v-if="employee.addressLine2">, {{ employee.addressLine2 }}</span>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>
            <v-col cols="12" md="4" class="p-0">
              <v-list dense>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">City</v-list-item-title>
                    <v-list-item-subtitle>{{ employee.city || 'N/A' }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">State</v-list-item-title>
                    <v-list-item-subtitle>{{ employee.state || 'N/A' }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>
            <v-col cols="12" md="4" class="p-0">
              <v-list dense>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">Pin Code</v-list-item-title>
                    <v-list-item-subtitle>{{ employee.pinCode || 'N/A' }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>

          <!-- Employment Information -->
          <v-row class="mt-4">
            <v-col cols="12" class="p-0">
              <h3 class="text-h6 font-weight-bold px-4 underline">Employment Information</h3>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="4" class="p-0">
              <v-list dense>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">CTC</v-list-item-title>
                    <v-list-item-subtitle>{{
                      formatCurrency(employee.ctc) || 'N/A'
                    }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>
            <v-col cols="12" md="4" class="p-0">
              <v-list dense>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold"
                      >Reporting Manager</v-list-item-title
                    >
                    <v-list-item-subtitle>{{
                      getManagerName(employee.reportesTo) || 'N/A'
                    }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>
            <v-col cols="12" md="4" class="p-0">
              <v-list dense>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">Work Location</v-list-item-title>
                    <v-list-item-subtitle>{{
                      employee.employmentDetails?.workLocation || 'N/A'
                    }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import UserAvatar from '@/components/UserAvatar.vue'

export default {
  name: 'DigitalProfile',
  components: {
    UserAvatar
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    employee: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      dialog: false,
      fallbackImage: require('@/assets/img/profileOther.png')
    }
  },
  computed: {
    profileImageUrl() {
      if (this.employee?.profileImage) {
        return `${process.env.VUE_APP_S3_BASE_URL}${this.employee.profileImage}`
      }
      return this.fallbackImage
    }
  },
  watch: {
    visible(newVal) {
      this.dialog = newVal
    }
  },
  methods: {
    closeDialog() {
      this.$emit('handleDigitalProfileClose')
    },
    onImageError() {
      // Image error handling is handled by the computed property
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    formatCurrency(amount) {
      if (!amount) return 'N/A'
      return `₹${amount.toLocaleString('en-IN')}`
    },
    getManagerName(manager) {
      if (!manager) return 'N/A'
      return `${manager.firstName} ${manager.lastName}`
    },
    getStatusColor(status) {
      const statusColors = {
        ACTIVE: 'success',
        INACTIVE: 'warning',
        CLOSED: 'error'
      }
      return statusColors[status] || 'grey'
    }
  }
}
</script>
